import { ReactNode } from 'react'
import cn from 'utils/cn'
import Button from 'core/components/buttons/Button'
import Icon from 'core/components/icons/Icon'

/**
 * Props for the LinkButton component.
 */
interface Props {
  /**
   * Text or elements displayed inside the button.
   */
  text: ReactNode | string

  /**
   * Function called when the button is clicked.
   */
  onClick?: VoidFunction

  /**
   * URL to open when the button is clicked.
   * If provided, the link will open in a new tab.
   */
  url?: string

  /**
   * Features string passed to `window.open` (e.g., "noopener,noreferrer").
   * Only used when `url` is provided.
   */
  newWindowFeatures?: string

  /**
   * Additional CSS classes for custom styling.
   */
  className?: string

  /**
   * Forces the "open in new tab" icon to appear,
   * even if no `url` is provided.
   */
  forceOpenIcon?: boolean
}

/**
 * A button styled as a link that optionally opens a URL in a new tab.
 * Can also include a suffix icon to indicate external navigation.
 */
const LinkButton = ({
  text,
  url,
  onClick,
  className,
  newWindowFeatures,
  forceOpenIcon
}: Props) => {
  return (
    <Button
      variant="link"
      className={cn('text-primary', className)}
      onClick={() => {
        if (onClick) onClick()
        if (url) window.open(url, '_blank', newWindowFeatures)
      }}
      suffix={url || forceOpenIcon ? <Icon name="open" size={14} /> : undefined}
    >
      {text}
    </Button>
  )
}

export default LinkButton
